import { zodResolver } from '@hookform/resolvers/zod';
import { UserStatuses } from '@prisma/client';
import { ValidationResultResponse } from '@zond/api-interfaces';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@zond/ui';
import { setFormErrors } from '@zond/utils';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '../../auth-provider';
import { useSignUp } from '../users/user.queries';
import { GoogleOAuthButton } from './components/GoogleOAuthButton';

export function SignUpForm() {
  const { t } = useTranslation('auth');
  const signUpSchema = z.object({
    firstname: z.string().min(1, t('validation.firstNameRequired')),
    lastname: z.string().min(1, t('validation.lastNameRequired')),
    email: z.string().email(t('validation.invalidEmail')),
    password: z.string().min(8, t('validation.passwordMin')),
    timezone: z.string().min(1, t('validation.timezone')).optional(),
  });
  const navigate = useNavigate();
  const { mutate: signUp, data, isPending, isSuccess } = useSignUp();
  const [serverError, setServerError] = useState('');
  const [registered, setRegistered] = useState(false);

  const form = useForm({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      password: '',
    },
    mode: 'onChange', // Add this line to enable real-time validation
  });

  const { setToken } = useAuth();

  if (isSuccess) {
    console.log('isSuccess', data);
    if (data.status === UserStatuses.Active) {
      localStorage.setItem('refreshToken', data.refreshToken);
      setTimeout(() => {
        setToken(data.accessToken);
        console.log('navigating to /');
        navigate('/');
      }, 0);
    } else {
      if (!registered) {
        setRegistered(true);
      }
    }
  }

  const onSubmit = (values: z.infer<typeof signUpSchema>) => {
    setServerError('');
    signUp(values, {
      onError: (error) => {
        if (error?.response?.data) {
          setFormErrors(form, error.response.data as ValidationResultResponse);
        } else {
          setServerError(t('signUp.errors.failed'));
        }
      },
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t('signUp.title')}</CardTitle>
        </CardHeader>
        {registered ? (
          <CardContent>
            <p className="text-center">{t('signUp.success')}</p>
          </CardContent>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardContent className="grid gap-4">
                <GoogleOAuthButton onError={setServerError} />
                <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                  <span className="relative z-10 bg-background px-2 text-muted-foreground">
                    {t('signUp.orEnterDetails')}
                  </span>
                </div>
                <div className="grid gap-2">
                  <FormField
                    control={form.control}
                    name="firstname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('signUp.firstNameLabel')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('signUp.firstNamePlaceholder')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('signUp.lastNameLabel')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('signUp.lastNamePlaceholder')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('common:labels.email')}</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="m@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('signUp.passwordLabel')}</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="********" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                {serverError && <div className="text-red-500 text-sm text-center">{serverError}</div>}
              </CardContent>
              <CardFooter className="flex flex-col gap-2">
                <Button className="w-full" type="submit" disabled={isPending || !form.formState.isValid}>
                  {isPending ? t('signUp.submitting') : t('signUp.submit')}
                </Button>
                <div className="text-sm text-center">
                  {t('signUp.haveAccount')}{' '}
                  <Button variant="link" className="px-0" asChild>
                    <Link to="/login">{t('signUp.signInLink')}</Link>
                  </Button>
                </div>
              </CardFooter>
            </form>
          </Form>
        )}
      </Card>
      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary">
        <Trans
          t={t}
          i18nKey="legal.agreement"
          components={{ terms: <Link to="/terms" />, privacy: <Link to="/privacy" /> }}
        />
      </div>
    </div>
  );
}
